import React, { useRef } from 'react';


// --- Reusable 6-Digit Input Component (DigitInputs) ---
const DigitInputs = React.memo(({ digits, setDigits, disabled = false, isPasskey = false }) => {
  const refs = useRef([]);
  // Determines if the input shows characters (text) or dots (password)
  const inputType = isPasskey ? "password" : "text";

  const handleChange = (index, value) => {
    // Allows only one numeric digit
    const numericValue = value.replace(/\D/g, '').slice(0, 1);
    // Prevents non-numeric input from clearing the box
    if (numericValue.length === 0 && value.length > 0) return;

    const newDigits = [...digits];
    newDigits[index] = numericValue;
    setDigits(newDigits);

    // Auto-focus to the next input only if a digit was entered
    if (numericValue && index < 5 && refs.current[index + 1]) {
      refs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace: moves back and clears the previous box if current is empty
    if (e.key === 'Backspace' && !digits[index] && index > 0 && refs.current[index - 1]) {
      e.preventDefault();
      refs.current[index - 1].focus();
      const newDigits = [...digits];
      newDigits[index - 1] = '';
      setDigits(newDigits);
    }
  };

  return (
    <div className="digit-inputs" style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
      {digits.map((digit, index) => (
        <input
          key={index}
          type={inputType}
          maxLength="1"
          name={`${isPasskey ? 'passkey' : 'otp'}-digit-${index}`}
          value={digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          required
          inputMode="numeric"
          pattern="\d{1}"
          disabled={disabled}
          ref={(el) => (refs.current[index] = el)}
          // --- Reapplied consistent UI style ---
          style={{ width: '50px', height: '50px', textAlign: 'center', fontSize: '18px', border: '1px solid #ccc', borderRadius: '4px' }}
        // -------------------------------------
        />
      ))}
    </div>
  );
});

// Since DigitInputs is a reusable sub-component, it's good practice to export it.
export { DigitInputs };

// --- Main SecurityStep Component ---
const SecurityStep = React.memo(({
  // jmiOfficerID removed - officer verification no longer required
  // IMPORTANT: These MUST be 6-element arrays in the parent component: useState(['', '', '', '', '', ''])
  privatePasskey,
  setPrivatePasskey,
  confirmPasskey,
  setConfirmPasskey,
  errors,
  setErrors,
}) => {
  // Safely join the array digits into strings for validation and API calls
  const fullPrivatePasskey = Array.isArray(privatePasskey) ? privatePasskey.join('') : '';
  const fullConfirmPasskey = Array.isArray(confirmPasskey) ? confirmPasskey.join('') : '';

 
  // --- PASSKEY VALIDATION LOGIC (Effect) ---
  React.useEffect(() => {
    let newErrors = {};



    // Validation for Private Passkey length
    if (fullPrivatePasskey.length > 0 && fullPrivatePasskey.length < 6) {
      newErrors.privatePasskey = 'Passkey must be exactly 6 digits.';
    }

    // Validation for Confirm Passkey length
    if (fullConfirmPasskey.length > 0 && fullConfirmPasskey.length < 6) {
      newErrors.confirmPasskey = 'Passkey must be exactly 6 digits.';
    }

    // Check for match only if both are full length (6 digits)
    if (fullPrivatePasskey.length === 6 && fullConfirmPasskey.length === 6) {
      if (fullPrivatePasskey !== fullConfirmPasskey) {
        newErrors.passkeysMatch = 'Passkeys do not match.';
      }
    }


  }, [fullPrivatePasskey, fullConfirmPasskey, setErrors]);


  // --- COMPONENT RENDER ---
  return (
    <div className="step-content">
     
      {/* Private Passkey Section - Now using 6-Digit Input UI */}
      <div
        className="security-section"
        style={{
          marginTop: '24px'
        }}
      >
        <h3>Set Private Passkey</h3>
        <div className="form-group" style={{ marginBottom: '20px' }}>
          <label>Private Passkey (6-digits) <span className="required-asterisk">*</span></label>
          <DigitInputs
            digits={privatePasskey}
            setDigits={setPrivatePasskey}
           
            isPasskey={true} // Passkey: password input type (dots)
          />
          {errors.privatePasskey && <span className="error-message" style={{ color: 'red', fontSize: '12px' }}>{errors.privatePasskey}</span>}
        </div>

        <div className="form-group">
          <label>Re-enter Private Passkey <span className="required-asterisk">*</span></label>
          <DigitInputs
            digits={confirmPasskey}
            setDigits={setConfirmPasskey}
       
            isPasskey={true} // Passkey: password input type (dots)
          />
          {errors.confirmPasskey && <span className="error-message" style={{ color: 'red', fontSize: '12px' }}>{errors.confirmPasskey}</span>}
          {errors.passkeysMatch && <span className="error-message" style={{ color: 'red', fontSize: '12px' }}>{errors.passkeysMatch}</span>}
        </div>
      </div>
    </div>
  );
});

export default SecurityStep;
